<script setup lang="ts">
  import { CalendarDate } from '@internationalized/date';
  import dayjs from 'dayjs';
  import utc from 'dayjs/plugin/utc';
  import type { DateRange } from 'reka-ui';
  import { computed, nextTick, shallowRef, watch } from 'vue';

  import { Button } from '@ui/components/button';
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
  } from '@ui/components/dropdown-menu';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { RangeCalendar } from '@ui/components/range-calendar';
  import { cn } from '@ui/utils/utils';

  const props = defineProps<{
    modelValue?: DateRange | null;
    /** Strips the border box, label pill, and clear button. Use with a custom slot trigger. */
    plain?: boolean;
  }>();

  const emits = defineEmits<{
    (e: 'update:modelValue', range: DateRange | null): void;
  }>();

  dayjs.extend(utc);

  const dropdownOpen = shallowRef(false);
  const calendarOpen = shallowRef(false);
  const selectedRange = shallowRef<DateRange | null>(props.modelValue ?? null);

  watch(
    () => props.modelValue,
    (newValue) => {
      selectedRange.value = newValue ?? null;
    },
  );

  const hasValue = (range: DateRange | null): range is DateRange & { start: object; end: object } =>
    !!(range?.start && range?.end);

  function formatDate(d: { toString: () => string }) {
    return dayjs.utc(d.toString()).format('MMM D');
  }

  function toCalendarDate(d: dayjs.Dayjs): CalendarDate {
    return new CalendarDate(d.year(), d.month() + 1, d.date());
  }

  function applyPreset(start: dayjs.Dayjs, end: dayjs.Dayjs) {
    const range: DateRange = {
      start: toCalendarDate(start),
      end: toCalendarDate(end),
    };
    selectedRange.value = range;
    emits('update:modelValue', range);
  }

  function clear() {
    selectedRange.value = null;
    emits('update:modelValue', null);
  }

  function openCalendar() {
    dropdownOpen.value = false;
    nextTick(() => {
      calendarOpen.value = true;
    });
  }

  const presets = computed(() => {
    // Reading dropdownOpen ensures this recalculates each time the dropdown opens,
    // so labels and date ranges stay fresh if the tab is kept open past midnight.
    void dropdownOpen.value;
    const now = dayjs.utc();
    return [
      {
        label: 'Today',
        detail: now.format('ddd MMM D'),
        apply: () => applyPreset(now, now),
      },
      {
        label: 'Yesterday',
        detail: now.subtract(1, 'day').format('ddd MMM D'),
        apply: () => applyPreset(now.subtract(1, 'day'), now.subtract(1, 'day')),
      },
      {
        label: 'Last 3 Days',
        detail: `${now.subtract(2, 'day').format('MMM D')} – ${now.format('MMM D')}`,
        apply: () => applyPreset(now.subtract(2, 'day'), now),
      },
      {
        label: 'Last 7 Days',
        detail: `${now.subtract(6, 'day').format('MMM D')} – ${now.format('MMM D')}`,
        apply: () => applyPreset(now.subtract(6, 'day'), now),
      },
      {
        label: 'Last 30 Days',
        detail: `${now.subtract(1, 'month').format('MMM D')} – ${now.format('MMM D')}`,
        apply: () => applyPreset(now.subtract(1, 'month'), now),
      },
    ];
  });

  const formattedLabel = computed<string | null>(() => {
    if (!hasValue(selectedRange.value)) return null;
    if (selectedRange.value.start.toString() === selectedRange.value.end.toString()) {
      return formatDate(selectedRange.value.start);
    }
    return `${formatDate(selectedRange.value.start)} – ${formatDate(selectedRange.value.end)}`;
  });
</script>

<template>
  <div
    v-bind="$attrs"
    :class="
      cn(
        'relative flex items-center',
        plain ? 'h-auto bg-transparent' : 'h-8 rounded-sm border truncate',
      )
    "
  >
    <!-- Invisible anchor for the calendar popover, positioned over the trigger -->
    <Popover v-model:open="calendarOpen">
      <PopoverTrigger as-child>
        <span class="absolute inset-0 pointer-events-none" />
      </PopoverTrigger>
      <PopoverContent class="w-auto p-0" align="start">
        <!-- @vue-ignore -->
        <RangeCalendar
          v-model="selectedRange"
          @update:model-value="
            (r) => {
              if (r?.start && r?.end) {
                calendarOpen = false;
                emits('update:modelValue', r as DateRange);
              }
            }
          "
        />
      </PopoverContent>
    </Popover>

    <template v-if="!plain && hasValue(selectedRange)">
      <div class="flex text-xs gap-2 px-3 items-center font-medium h-full border-l-md">
        <i class="fa fa-calendar" />
        Date
      </div>
    </template>

    <DropdownMenu v-model:open="dropdownOpen">
      <DropdownMenuTrigger as-child>
        <slot :formatted-label="formattedLabel">
          <Button
            variant="ghost"
            size="sm"
            :class="cn(!plain && hasValue(selectedRange) && 'rounded-none border-l')"
          >
            <i class="fa fa-calendar" />
            <span>{{ formattedLabel ?? 'All Time' }}</span>
          </Button>
        </slot>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" @close-auto-focus.prevent>
        <template v-if="plain && hasValue(selectedRange)">
          <DropdownMenuItem @click="clear()"> All Time </DropdownMenuItem>
          <DropdownMenuSeparator />
        </template>

        <DropdownMenuItem
          v-for="preset in presets"
          :key="preset.label"
          class="justify-between gap-6"
          @click="preset.apply()"
        >
          <span>{{ preset.label }}</span>
          <span class="text-muted-foreground font-normal">{{ preset.detail }}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem @click="openCalendar()">
          <i class="fa fa-calendar-days" />
          Custom Range
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <template v-if="!plain && hasValue(selectedRange)">
      <Button variant="ghost" size="sm" class="rounded-l-none border-l px-2" @click="clear()">
        <i class="far fa-xmark text-2xs" />
      </Button>
    </template>
  </div>
</template>
